import { AxiosError } from 'axios';
import type { ApiResponse } from '@shared/types';

/**
 * PT-6 B4 — what the Adjust dialog does with a failed Apply. A 409 is the stale path (reload and re-seed). A known
 * refusal shows its reason verbatim (O-8): a validator 400 its first sentence, any other 400 or a 404 its message. A
 * failure with no answer, a timeout or a 5xx gets the generic line with "please try again"; anything else the
 * generic line alone, because a false "try again" is worse than an honest "this failed". Typed values are kept.
 */
export type AdjustFailure = { kind: 'stale' } | { kind: 'message'; text: string };

export const ADJUST_FAILED_TRANSIENT = 'Couldn’t apply the adjustment. Nothing was changed — please try again.';
export const ADJUST_FAILED = 'Couldn’t apply the adjustment. Nothing was changed.';

export function classifyAdjustFailure(error: unknown): AdjustFailure {
  if (!(error instanceof AxiosError)) return { kind: 'message', text: ADJUST_FAILED };

  const status = error.response?.status;
  if (status === 409) return { kind: 'stale' };
  // No response at all (network down, timeout, aborted) or a server fault: nothing was applied, and it may pass.
  if (status === undefined || status >= 500) return { kind: 'message', text: ADJUST_FAILED_TRANSIENT };

  const body = error.response?.data as ApiResponse<unknown> | undefined;
  if (status === 400) {
    // A validator failure's message is always "Validation failed"; the sentence that helps is in `errors`.
    const first = Object.values(body?.errors ?? {}).flat().find((s) => typeof s === 'string' && s.trim());
    if (first) return { kind: 'message', text: first };
  }
  if ((status === 400 || status === 404) && body?.message) return { kind: 'message', text: body.message };

  // 403 (apiClient also toasts "Access Denied"), or a 4xx with no message.
  return { kind: 'message', text: ADJUST_FAILED };
}
